/**
 * BehaviorCard — one FINN value: the expected behaviors at this level (reference, no checklist),
 * a direct 1–5 rating (Nov 2026 cycle: Zohal, Oct 29), per-value detect from delivery,
 * collapsible context section (auto-opens when rated, manually openable before).
 */
import { useEffect, useRef, useState } from 'react';
import { C, BEHAVIORS, getBehaviorGroup, getRatingColor, getRatingLabel } from '../../data/managerReviewData';
import type { BehaviorData, FinnValue } from '../../types/managerReviewV2';
import ReviewTextarea from './ReviewTextarea';
import { detectBehaviorsForValue } from './behaviorDetect';

const renderBold = function(text: string): React.ReactNode {
  if (!text || !text.includes('**')) return text;
  const parts = text.split('**');
  return parts.map(function(p, i) { return i % 2 === 1 ? <strong key={i}>{p}</strong> : p; });
};

interface BehaviorCardProps {
  index: number;
  value: FinnValue;
  level: string;
  data: BehaviorData;
  /** Takes an updater, so async AI callbacks apply to the latest data, not a stale render snapshot. */
  onChange: (update: (prev: BehaviorData) => BehaviorData) => void;
  aiContext: Record<string, unknown>;
  nextBullets: string[] | null;
  nextLevelLabel: string | null;
  deliverables: Array<{ title?: string; wellDone?: string; improvement?: string }>;
  id?: string;
}

export default function BehaviorCard({
  index, value, level, data, onChange, aiContext,
  nextBullets, nextLevelLabel, deliverables, id,
}: BehaviorCardProps) {
  const group = getBehaviorGroup(level);
  const bullets = BEHAVIORS[group]?.[value.name] || [];

  const accentColor = data.rating ? getRatingColor(data.rating) : C.border;
  const [collapsed, setCollapsed] = useState(false);
  const [contextExpanded, setContextExpanded] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [detectError, setDetectError] = useState('');
  const [detectCount, setDetectCount] = useState<number | null>(null);

  // Auto-opens when rated; also manually openable before rating
  const showContext = contextExpanded || !!data.rating;
  const hasDeliveryNotes = deliverables.some(d => d.wellDone?.trim() || d.improvement?.trim());

  // The card is reused when the manager switches employee; a detection started for the previous
  // employee must not write into the next one's notes.
  const employeeKey = String(aiContext.employeeId ?? aiContext.employeeName ?? '');
  const employeeKeyRef = useRef(employeeKey);
  useEffect(() => { employeeKeyRef.current = employeeKey; }, [employeeKey]);

  const handleDetect = async () => {
    const startedFor = employeeKeyRef.current;
    setDetecting(true);
    setDetectError('');
    setDetectCount(null);
    try {
      const matches = await detectBehaviorsForValue(deliverables, value.name);
      if (startedFor !== employeeKeyRef.current) return;
      if (matches.length === 0) {
        setDetectCount(0);
      } else {
        // Merged into the LATEST data: the manager may have typed or rated while detection ran.
        const merge = (base: BehaviorData) => {
          let next = { ...base };
          let added = 0;
          matches.forEach(m => {
            const key = m.sentiment === 'positive' ? 'strengthsText' : 'improvementsText';
            const existing = next[key] || '';
            if (!existing.includes(m.excerpt.substring(0, 30))) {
              next = { ...next, [key]: (existing ? existing + '\n\n' : '') + '[From Delivery] ' + m.excerpt };
              added++;
            }
          });
          return { next, added };
        };
        onChange(function(prev) { return merge(prev).next; });
        setDetectCount(merge(data).added);
      }
    } catch {
      setDetectError('Detection failed — please try again.');
    } finally {
      setDetecting(false);
    }
  };

  // Behavior bullets as context for the coach
  const behaviorBullets = bullets.map(function(b) { return b.replace(/\*\*/g, ''); });

  return (
    <div id={id} style={{
      marginBottom: 12, borderRadius: 8, border: '1px solid #e8e8e8',
      borderLeft: '3px solid ' + accentColor, overflow: 'hidden', background: '#fff',
    }}>
      <div
        onClick={function() { setCollapsed(function(c) { return !c; }); }}
        style={{
          display: 'flex', alignItems: 'center', padding: '10px 16px',
          background: '#f5f5f5', borderBottom: collapsed ? 'none' : '1px solid #ebebeb',
          cursor: 'pointer', userSelect: 'none',
        }}
      >
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 13, color: '#111', letterSpacing: '-0.01em' }}>
          <span style={{ display: 'inline-block', transition: 'transform 0.15s', transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)', fontSize: 9 }}>▶</span>
          <div style={{
            width: 22, height: 22, borderRadius: 6, background: '#1a1a1a', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 10, fontWeight: 800, flexShrink: 0,
          }}>{index + 1}</div>
          {value.name}
        </div>
      </div>

      {!collapsed && (
        <>
          {/* What this value looks like at this level — reference for the rating, not a checklist */}
          <div style={{ padding: '10px 16px 12px', background: '#fff' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#999', marginBottom: 6 }}>Expected at {level}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {bullets.map(function(b, i) {
                return (
                  <div key={i} style={{ fontSize: 12, lineHeight: 1.6, color: '#333' }}>• {renderBold(b)}</div>
                );
              })}
            </div>
          </div>

          {nextBullets && nextBullets.length > 0 && (
            <div style={{ borderTop: '1px dashed #d8e6fa', background: '#f5f9ff', padding: '10px 16px' }}>
              <span style={{
                fontSize: 10, fontWeight: 700, color: C.blue,
                background: C.blue + '18', borderRadius: 4, padding: '2px 7px',
                letterSpacing: '0.06em', textTransform: 'uppercase',
              }}>{nextLevelLabel}</span>
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 5 }}>
                {nextBullets.map(function(b, i) {
                  return (
                    <div key={i} style={{ fontSize: 12, color: C.blue + 'aa', lineHeight: 1.6 }}>
                      • {renderBold(b)}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{ padding: '12px 16px', background: '#f5f5f5', borderTop: '1px solid #ebebeb' }}>
            {/* Rating 1–5, 5 = best */}
            <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 8 }}>
              Rating
            </div>
            {(
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <div style={{ display: 'flex', gap: 5 }}>
                  {[1, 2, 3, 4, 5].map(function(r) {
                    const isSelected = data.rating === r;
                    const col = getRatingColor(r);
                    return (
                      <button
                        key={r}
                        onClick={function(e) { e.stopPropagation(); onChange(function(prev) { return { ...prev, rating: prev.rating === r ? null : r }; }); }}
                        style={{
                          width: 34, height: 34, borderRadius: 6,
                          border: '1.5px solid ' + (isSelected ? col : '#d1d5db'),
                          background: isSelected ? col : '#fff',
                          color: isSelected ? '#fff' : '#111',
                          fontSize: 13, fontWeight: 700, cursor: 'pointer',
                          fontFamily: 'Inter', transition: 'all 0.1s',
                        }}
                      >{r}</button>
                    );
                  })}
                </div>
                <span style={{ fontSize: 12, color: data.rating ? getRatingColor(data.rating) : '#999', fontWeight: data.rating ? 600 : 400 }}>
                  {data.rating ? getRatingLabel(data.rating) : 'Select a rating'}
                </span>
              </div>
            )}

            {/* Step 3: Context — collapsed by default, auto-opens when rated */}
            <div
              onClick={() => setContextExpanded(e => !e)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                cursor: 'pointer', paddingTop: 8, marginTop: 2,
                borderTop: '1px solid #e2e2e2', userSelect: 'none',
              }}
            >
              <span style={{
                display: 'inline-block', transition: 'transform 0.15s',
                transform: showContext ? 'rotate(90deg)' : 'rotate(0deg)', fontSize: 9,
              }}>▶</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#555' }}>
                Add context
              </span>
              {!data.rating && !contextExpanded && (
                <span style={{ fontSize: 11, color: '#bbb', fontStyle: 'italic' }}>· rate this value first</span>
              )}
            </div>

            {showContext && (
              <div style={{ marginTop: 12 }}>
                {/* Per-value detect button */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <button
                    onClick={handleDetect}
                    disabled={detecting || !hasDeliveryNotes}
                    title={!hasDeliveryNotes ? 'Add notes in the Delivery tab first' : `Scan delivery notes for evidence of ${value.name}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '5px 11px', borderRadius: 7, fontSize: 11, fontWeight: 600,
                      cursor: detecting || !hasDeliveryNotes ? 'not-allowed' : 'pointer',
                      fontFamily: 'Inter', transition: 'all 0.15s',
                      border: `1.5px solid ${detecting ? '#d1d5db' : hasDeliveryNotes ? C.blue : '#ddd'}`,
                      background: detecting ? '#f5f5f5' : hasDeliveryNotes ? C.lightBlue : '#fafafa',
                      color: detecting ? '#aaa' : hasDeliveryNotes ? C.blue : '#bbb',
                      opacity: !hasDeliveryNotes ? 0.55 : 1,
                    }}
                  >
                    {detecting ? '⏳ Detecting…' : '🔍 Detect from delivery'}
                  </button>
                  {!detecting && detectCount !== null && detectCount > 0 && (
                    <span style={{ fontSize: 11, color: C.green, fontWeight: 600 }}>
                      ✓ {detectCount} insight{detectCount !== 1 ? 's' : ''} added
                    </span>
                  )}
                  {!detecting && detectCount === 0 && !detectError && (
                    <span style={{ fontSize: 11, color: C.muted, fontWeight: 500 }}>
                      No new insights found
                    </span>
                  )}
                  {detectError && (
                    <span style={{ fontSize: 11, color: '#e8294a', fontWeight: 600 }}>{detectError}</span>
                  )}
                </div>

                {index === 0 && (
                  <div style={{ fontSize: 10, color: '#bbb', lineHeight: 1.5, marginBottom: 14, fontStyle: 'italic' }}>
                    💡 Delivery notes are a starting point — also note day-to-day moments like team meetings, ad-hoc support, or FINN Station shifts.
                  </div>
                )}

                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 6 }}>
                    Where did I see this behavior clearly demonstrated?
                  </div>
                  <ReviewTextarea
                    value={data.strengthsText || ''}
                    onChange={function(v) { onChange(function(prev) { return { ...prev, strengthsText: v }; }); }}
                    placeholder="What did they do particularly well for this value? Be specific. Type or hit Record to speak your thoughts."
                    voice
                    compact
                    aiContext={{ ...aiContext, field: 'strengths', availableBehaviors: behaviorBullets }}
                    onRedirected={function(text) { onChange(function(prev) { return { ...prev, improvementsText: (prev.improvementsText ? prev.improvementsText + '\n\n' : '') + text }; }); }}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 6 }}>
                    Were there any gaps or inconsistencies in this behavior?
                  </div>
                  <ReviewTextarea
                    value={data.improvementsText || ''}
                    onChange={function(v) { onChange(function(prev) { return { ...prev, improvementsText: v }; }); }}
                    placeholder="Where could they have demonstrated this value more strongly? Type or hit Record to speak your thoughts."
                    voice
                    compact
                    aiContext={{ ...aiContext, field: 'improvements', availableBehaviors: behaviorBullets }}
                  />
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
